import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, 
  SignOut, 
  Truck, 
  CurrencyEur, 
  Clock, 
  CheckCircle,
  Gear,
  DownloadSimple,
  ChartBar,
  MapTrifold,
  X,
  CaretDown,
  CaretUp,
  Prohibit,
  Package,
  FileText
} from '@phosphor-icons/react';
import InterventionCard from '../components/app/InterventionCard';
import InterventionForm from '../components/app/InterventionForm';
import InterventionDetail from '../components/app/InterventionDetail';
import InterventionEditForm from '../components/app/InterventionEditForm';
import TechnicianMap from '../components/app/TechnicianMap';
import StockNotifications from '../components/app/StockNotifications';
import InvoiceHistory from '../components/app/InvoiceHistory';
import InterventionListView from '../components/app/InterventionListView';
import ManualInvoiceDialog from '../components/app/ManualInvoiceDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const getDateStr = (isoString) => {
  if (!isoString) return null;
  return isoString.split('T')[0];
};

const getTodayStr = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const formatDayHeader = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [interventions, setInterventions] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, cashed_total: 0 });
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [stockPendingCount, setStockPendingCount] = useState(0);
  const [editingIntervention, setEditingIntervention] = useState(null);
  const [manualInvoiceOpen, setManualInvoiceOpen] = useState(false);

  const fetchInterventions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/interventions`, {
        headers: getAuthHeaders()
      });
      setInterventions(response.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des interventions');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/stats/today`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (err) {
      // Ignore
    }
  }, []);

  useEffect(() => {
    fetchInterventions();
    fetchStats();
    const fetchStockCount = async () => {
      try {
        const r = await axios.get(`${API}/api/stock/notifications`, { headers: getAuthHeaders() });
        setStockPendingCount(r.data.pending_count || 0);
      } catch {}
    };
    fetchStockCount();
    const interval = setInterval(() => {
      fetchInterventions();
      fetchStats();
      fetchStockCount();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchInterventions, fetchStats]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/api/interventions/export/csv`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `interventions_${getTodayStr()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch (err) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleValidate = async (id) => {
    try {
      await axios.post(
        `${API}/api/interventions/${id}/status`,
        { status: 'validee' },
        { headers: getAuthHeaders() }
      );
      toast.success('Intervention validée');
      fetchInterventions();
      fetchStats();
    } catch (err) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Voulez-vous vraiment annuler cette intervention ?')) return;
    try {
      await axios.delete(`${API}/api/interventions/${id}`, { headers: getAuthHeaders() });
      toast.success('Intervention annulée');
      fetchInterventions();
      fetchStats();
    } catch (err) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const today = getTodayStr();

  // TAB 1: Aujourd'hui — scheduled for today
  const todayPending = interventions.filter(i => 
    i.status === 'en_attente' && getDateStr(i.announced_time) === today
  );
  const STATUS_PRIORITY = { en_route: 0, en_cours: 1, validee: 2, terminee: 3, encaissee: 4 };

  const todayValidated = interventions.filter(i => 
    ['validee', 'en_route', 'en_cours', 'terminee', 'encaissee'].includes(i.status) &&
    getDateStr(i.announced_time) === today
  ).sort((a, b) => {
    const pa = STATUS_PRIORITY[a.status] ?? 5;
    const pb = STATUS_PRIORITY[b.status] ?? 5;
    if (pa !== pb) return pa - pb;
    return (a.announced_time || '').localeCompare(b.announced_time || '');
  });

  // TAB 2: Plus tard — from tomorrow onwards, non-cancelled
  const laterInterventions = interventions.filter(i => {
    const d = getDateStr(i.announced_time);
    return d && d > today && i.status !== 'annulee';
  }).sort((a, b) => (a.announced_time || '').localeCompare(b.announced_time || ''));

  const laterGrouped = groupByAnnouncedDate(laterInterventions, true);

  // TAB 3: Historique — before today, non-cancelled
  const historyInterventions = interventions.filter(i => {
    const d = getDateStr(i.announced_time);
    return d && d < today && i.status !== 'annulee';
  }).sort((a, b) => (b.announced_time || '').localeCompare(a.announced_time || ''));

  const historyGrouped = groupByAnnouncedDate(historyInterventions, false);

  // TAB 4: Annulées — all cancelled
  const cancelledInterventions = interventions.filter(i => i.status === 'annulee')
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  const canAccessSettings = user?.role === 'admin';
  const canSeeCA = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Truck size={28} weight="fill" className="text-reco-500" />
              <h1 className="text-xl font-heading font-bold text-gray-900">
                <span className="text-reco-500">reco</span>pneus
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-500 text-sm hidden sm:block">
                {user?.name} ({user?.role})
              </span>
              {canAccessSettings && (
                <Button variant="ghost" size="icon" onClick={() => navigate('/reports')}
                  className="text-gray-500 hover:text-gray-700" data-testid="reports-button">
                  <ChartBar size={20} />
                </Button>
              )}
              {canAccessSettings && (
                <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}
                  className="text-gray-500 hover:text-gray-700" data-testid="settings-button">
                  <Gear size={20} />
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={handleLogout}
                className="text-gray-500 hover:text-gray-700" data-testid="logout-button">
                <SignOut size={20} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className={`grid gap-4 ${canSeeCA ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`}>
            <StatCard icon={<Truck size={20} />} label="Interventions" value={stats.total || 0} color="text-reco-500" />
            <StatCard icon={<Clock size={20} />} label="En attente" value={stats.pending || 0} color="text-amber-500" />
            <StatCard icon={<CheckCircle size={20} />} label="Terminées" value={stats.completed || 0} color="text-green-500" />
            {canSeeCA && stats.cashed_total !== null && (
              <StatCard icon={<CurrencyEur size={20} />} label="CA du jour" value={`${(stats.cashed_total || 0).toFixed(2)} €`} color="text-reco-600" />
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-heading font-bold text-gray-900">Interventions</h2>
          <div className="flex items-center gap-3">
            {user?.role === 'admin' && (
              <Button variant="outline" onClick={handleExportCSV}
                className="border-gray-300 hover:bg-gray-100 gap-2" data-testid="export-csv-button">
                <DownloadSimple size={18} />
                <span className="hidden sm:inline">Export CSV</span>
              </Button>
            )}
            <Button variant="outline" onClick={() => setManualInvoiceOpen(true)}
              className="border-reco-500 text-reco-600 hover:bg-reco-50 gap-2" data-testid="manual-invoice-button">
              <FileText size={18} />
              <span className="hidden sm:inline">Facture manuelle</span>
            </Button>
            <Button onClick={() => setFormOpen(true)}
              className="bg-reco-500 hover:bg-reco-600 text-white gap-2 rounded-full px-6" data-testid="new-intervention-button">
              <Plus size={18} weight="bold" />
              Nouvelle intervention
            </Button>
          </div>
        </div>

        <Tabs defaultValue="today" className="space-y-6">
          <TabsList className="bg-gray-100 border border-gray-200 p-1 rounded-full flex-wrap">
            <TabsTrigger value="today"
              className="rounded-full data-[state=active]:bg-reco-500 data-[state=active]:text-white"
              data-testid="tab-today">
              Aujourd'hui ({todayPending.length + todayValidated.length})
            </TabsTrigger>
            <TabsTrigger value="later"
              className="rounded-full data-[state=active]:bg-reco-500 data-[state=active]:text-white"
              data-testid="tab-later">
              Plus tard ({laterInterventions.length})
            </TabsTrigger>
            <TabsTrigger value="history"
              className="rounded-full data-[state=active]:bg-reco-500 data-[state=active]:text-white"
              data-testid="tab-history">
              Historique ({historyInterventions.length})
            </TabsTrigger>
            <TabsTrigger value="cancelled"
              className="rounded-full data-[state=active]:bg-reco-500 data-[state=active]:text-white"
              data-testid="tab-cancelled">
              Annulées ({cancelledInterventions.length})
            </TabsTrigger>
            <TabsTrigger value="invoices"
              className="rounded-full data-[state=active]:bg-reco-500 data-[state=active]:text-white"
              data-testid="tab-invoices">
              <FileText size={14} className="mr-1" />
              Historique factures
            </TabsTrigger>
            <TabsTrigger value="stock"
              className="rounded-full data-[state=active]:bg-reco-500 data-[state=active]:text-white relative"
              data-testid="tab-stock">
              Stock
              {stockPendingCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                  {stockPendingCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: Aujourd'hui */}
          <TabsContent value="today">
            {/* Map Toggle */}
            <div className="mb-6">
              <button
                onClick={() => setMapOpen(!mapOpen)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm w-full sm:w-auto"
                data-testid="map-toggle"
              >
                <MapTrifold size={18} className="text-reco-500" />
                {mapOpen ? 'Masquer la carte' : 'Voir la carte des techniciens'}
                {mapOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
              </button>
              {mapOpen && (
                <div className="mt-3" data-testid="map-container">
                  <TechnicianMap interventions={interventions} />
                </div>
              )}
            </div>

            {loading ? <LoadingState /> : (
              <div className="space-y-8">
                {/* Sub-section: En attente */}
                {todayPending.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-amber-600 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <Clock size={16} />
                      En attente ({todayPending.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {todayPending.map(intervention => (
                        <InterventionCard
                          key={intervention.id}
                          intervention={intervention}
                          onValidate={handleValidate}
                          onCancel={handleCancel}
                          onSelect={setSelectedIntervention}
                          showValidateButton
                          showCancelButton
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub-section: Validées */}
                {todayValidated.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-reco-600 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <CheckCircle size={16} />
                      Validées ({todayValidated.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {todayValidated.map(intervention => (
                        <InterventionCard
                          key={intervention.id}
                          intervention={intervention}
                          onSelect={setSelectedIntervention}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {todayPending.length === 0 && todayValidated.length === 0 && (
                  <EmptyState message="Aucune intervention prévue aujourd'hui" />
                )}
              </div>
            )}
          </TabsContent>

          {/* TAB 2: Plus tard */}
          <TabsContent value="later">
            {loading ? <LoadingState /> : laterInterventions.length === 0 ? (
              <EmptyState message="Aucune intervention à venir" />
            ) : (
              <div className="space-y-8">
                {laterGrouped.map(([date, items]) => (
                  <div key={date}>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                      {capitalizeFirst(formatDayHeader(date))}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {items.map(intervention => (
                        <InterventionCard
                          key={intervention.id}
                          intervention={intervention}
                          onSelect={setSelectedIntervention}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TAB 3: Historique */}
          <TabsContent value="history">
            {loading ? <LoadingState /> : (
              <InterventionListView
                interventions={historyInterventions}
                onSelect={setSelectedIntervention}
                scope="history"
                title="Historique des interventions"
              />
            )}
          </TabsContent>

          {/* TAB 4: Annulées */}
          <TabsContent value="cancelled">
            {loading ? <LoadingState /> : (
              <InterventionListView
                interventions={cancelledInterventions}
                onSelect={setSelectedIntervention}
                scope="cancelled"
                title="Interventions annulées"
              />
            )}
          </TabsContent>

          {/* TAB 5: Stock */}
          <TabsContent value="stock">
            <StockNotifications />
          </TabsContent>

          {/* TAB 6: Factures */}
          <TabsContent value="invoices">
            <InvoiceHistory />
          </TabsContent>
        </Tabs>
      </main>

      {/* Modals */}
      <InterventionForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={() => { fetchInterventions(); fetchStats(); }}
      />
      <InterventionDetail
        intervention={selectedIntervention}
        onClose={() => setSelectedIntervention(null)}
        onUpdate={() => { fetchInterventions(); fetchStats(); }}
        onEdit={(intervention) => setEditingIntervention(intervention)}
      />
      <InterventionEditForm
        intervention={editingIntervention}
        isOpen={!!editingIntervention}
        onClose={() => setEditingIntervention(null)}
        onSuccess={() => { fetchInterventions(); fetchStats(); }}
      />
      <ManualInvoiceDialog
        open={manualInvoiceOpen}
        onOpenChange={setManualInvoiceOpen}
      />
    </div>
  );
}

function groupByAnnouncedDate(items, ascending) {
  const groups = {};
  items.forEach(item => {
    const date = getDateStr(item.announced_time) || 'Inconnu';
    if (!groups[date]) groups[date] = [];
    groups[date].push(item);
  });
  return Object.entries(groups).sort((a, b) =>
    ascending ? a[0].localeCompare(b[0]) : b[0].localeCompare(a[0])
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <div className={`${color} mb-2`}>{icon}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-4" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
            <div className="h-4 bg-gray-200 rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
      <Truck size={48} className="mx-auto text-gray-300 mb-4" />
      <p className="text-gray-500">{message}</p>
    </div>
  );
}
